import { Preferences } from "@capacitor/preferences";

const GCLID_KEY = "ads_gclid";
const GCLID_EXPIRY_KEY = "ads_gclid_expiry";

const FB_AD_ID_KEY = "ads_fb_ad_id";
const FB_AD_ID_EXPIRY_KEY = "ads_fb_ad_id_expiry";

const UTM_KEY = "ads_utm";
const UTM_EXPIRY_KEY = "ads_utm_expiry";

const GCLID_TTL = 90 * 24 * 60 * 60 * 1000; // 90 days
const FB_AD_ID_TTL = 30 * 24 * 60 * 60 * 1000; // 30 days
const UTM_TTL = 30 * 24 * 60 * 60 * 1000; // 30 days

const UTM_PARAMS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
] as const;

type UtmKey = typeof UTM_PARAMS[number];


export interface AdsTrackingData {
    gclid?: string;
    fb_ad_id?: string;
    utm?: Partial<Record<UtmKey, string>>;
}

export interface AdsTrackingValueWithExpiry<T> {
    value: T;
    expiry: number;
}

export interface AdsTrackingDataWithExpiry {
    gclid?: AdsTrackingValueWithExpiry<string>;
    fb_ad_id?: AdsTrackingValueWithExpiry<string>;
    utm?: AdsTrackingValueWithExpiry<Partial<Record<UtmKey, string>>>;
}

let cached: AdsTrackingData | null = null;

let cachedWithExpiry: AdsTrackingDataWithExpiry | null = null;

const saveWithExpiry = async (key: string, expiryKey: string, value: string, ttl: number) => {
    const expiry = Date.now() + ttl;
    await Preferences.set({ key, value });
    await Preferences.set({ key: expiryKey, value: String(expiry) });
};

const loadWithExpiry = async (key: string, expiryKey: string): Promise<string | null> => {
    const { value } = await Preferences.get({ key });
    const { value: expiryStr } = await Preferences.get({ key: expiryKey });

    if (!value || !expiryStr) return null;

    const expiry = Number(expiryStr);
    if (Date.now() > expiry) {
        await Preferences.remove({ key });
        await Preferences.remove({ key: expiryKey });
        return null;
    }

    return value;
};

const loadWithExpiryMeta = async (
    key: string,
    expiryKey: string
): Promise<{ value: string; expiry: number } | null> => {
    const { value } = await Preferences.get({ key });
    const { value: expiryStr } = await Preferences.get({ key: expiryKey });

    if (!value || !expiryStr) return null;

    const expiry = Number(expiryStr);

    if (Date.now() > expiry) {
        await Preferences.remove({ key });
        await Preferences.remove({ key: expiryKey });
        return null;
    }

    return { value, expiry };
};

const setGclid = async (gclid: string) => {
    await saveWithExpiry(GCLID_KEY, GCLID_EXPIRY_KEY, gclid, GCLID_TTL);
};

const getGclid = async (): Promise<string | null> => {
    return await loadWithExpiry(GCLID_KEY, GCLID_EXPIRY_KEY);
};


const setFbAdId = async (fbAdId: string) => {
    await saveWithExpiry(FB_AD_ID_KEY, FB_AD_ID_EXPIRY_KEY, fbAdId, FB_AD_ID_TTL);
};

const getFbAdId = async (): Promise<string | null> => {
    return await loadWithExpiry(FB_AD_ID_KEY, FB_AD_ID_EXPIRY_KEY);
};


const setUtm = async (utm: Partial<Record<UtmKey, string>>) => {
    await saveWithExpiry(UTM_KEY, UTM_EXPIRY_KEY, JSON.stringify(utm), UTM_TTL);
};

const getUtm = async (): Promise<Partial<Record<UtmKey, string>> | null> => {
    const val = await loadWithExpiry(UTM_KEY, UTM_EXPIRY_KEY);
    if (!val) return null;
    try {
        return JSON.parse(val);
    } catch {
        return null;
    }
};

const extractFromUrl = () => {
    if (typeof window === "undefined") return null;

    const params = new URLSearchParams(window.location.search);

    const gclid = params.get("gclid") || undefined;
    const fb_ad_id = params.get("fb_ad_id") || undefined;

    const utm: Partial<Record<UtmKey, string>> = {};
    UTM_PARAMS.forEach((k) => {
        const v = params.get(k);
        if (v) utm[k] = v;
    });

    return { gclid, fb_ad_id, utm };
};

export const initAdsTracking = async (): Promise<void> => {
    const data = extractFromUrl();
    if (!data) return;

    if (data.gclid) {
        await setGclid(data.gclid);
    }

    if (data.fb_ad_id) {
        await setFbAdId(data.fb_ad_id);
    }

    if (data.utm && Object.keys(data.utm).length > 0) {
        await setUtm(data.utm);
    }

    cached = null;
    cachedWithExpiry = null;
};

export const getAdsTrackingData = async (): Promise<AdsTrackingData> => {
    if (cached) return cached;

    const [gclid, fb_ad_id, utm] = await Promise.all([
        getGclid(),
        getFbAdId(),
        getUtm(),
    ]);

    cached = {
        ...(gclid ? { gclid } : {}),
        ...(fb_ad_id ? { fb_ad_id } : {}),
        ...(utm ? { utm } : {}),
    };

    return cached;
};

export const getAdsTrackingDataWithExpiry = async (): Promise<AdsTrackingDataWithExpiry> => {
    if (cachedWithExpiry) return cachedWithExpiry;

    const [gclid, fb_ad_id, utm] = await Promise.all([
        loadWithExpiryMeta(GCLID_KEY, GCLID_EXPIRY_KEY),
        loadWithExpiryMeta(FB_AD_ID_KEY, FB_AD_ID_EXPIRY_KEY),
        loadWithExpiryMeta(UTM_KEY, UTM_EXPIRY_KEY),
    ]);

    const result: AdsTrackingDataWithExpiry = {};

    if (gclid) {
        result.gclid = gclid
    }

    if (fb_ad_id) {
        result.fb_ad_id = fb_ad_id
    }

    if (utm) {
        try {
            result.utm = {
                value: JSON.parse(utm.value),
                expiry: utm.expiry,
            };
        } catch {}
    }

    cachedWithExpiry = result;
    return result;
};

export const clearAdsTracking = async () => {
    cached = null;
    cachedWithExpiry = null;

    await Preferences.remove({ key: GCLID_KEY });
    await Preferences.remove({ key: GCLID_EXPIRY_KEY });

    await Preferences.remove({ key: FB_AD_ID_KEY });
    await Preferences.remove({ key: FB_AD_ID_EXPIRY_KEY });

    await Preferences.remove({ key: UTM_KEY });
    await Preferences.remove({ key: UTM_EXPIRY_KEY });
};
